import { X } from "lucide-react";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import type { InventoryItem } from "./inventaireTypes";
import { CATEGORIES } from "./inventaireTypes";

interface EditProductModalProps {
  item: InventoryItem;
  isOpen: boolean;
  onClose: () => void;
  onFieldUpdate: (id: string, field: keyof InventoryItem, value: any) => void;
}

const UNITES_MESURE = [
  { value: "unité", label: "unité" },
  { value: "ml", label: "ml (millilitres)" },
  { value: "L", label: "L (litres)" },
  { value: "g", label: "g (grammes)" },
  { value: "kg", label: "kg (kilogrammes)" },
  { value: "oz", label: "oz (onces)" },
  { value: "lb", label: "lb (livres)" },
  { value: "baril", label: "baril" },
  { value: "caisse", label: "caisse" },
  { value: "bouteille", label: "bouteille" },
  { value: "canette", label: "canette" },
  { value: "sac", label: "sac" },
  { value: "boîte", label: "boîte" },
] as const;

export function EditProductModal({ item, isOpen, onClose, onFieldUpdate }: EditProductModalProps) {
  const [formData, setFormData] = useState({
    nom: item.nom,
    categorie: item.categorie,
    typeItem: item.typeItem || "produit_simple",
    formatQuantite: item.formatQuantite || 0,
    formatUnite: item.formatUnite || "unité",
    formatPrecision: "", // Ex: "de 12 bouteilles" pour une caisse
    seuilStock: item.seuilStock || 10,
  });

  // Reset form when item changes
  useEffect(() => {
    setFormData({
      nom: item.nom,
      categorie: item.categorie,
      typeItem: item.typeItem || "produit_simple",
      formatQuantite: item.formatQuantite || 0,
      formatUnite: item.formatUnite || "unité",
      formatPrecision: "",
      seuilStock: item.seuilStock || 10,
    });
  }, [item]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFieldUpdate(item.id, "nom", formData.nom);
    onFieldUpdate(item.id, "categorie", formData.categorie);
    onFieldUpdate(item.id, "typeItem", formData.typeItem);
    onFieldUpdate(item.id, "formatQuantite", formData.formatQuantite > 0 ? formData.formatQuantite : undefined);
    onFieldUpdate(item.id, "formatUnite", formData.formatQuantite > 0 ? formData.formatUnite : undefined);
    onFieldUpdate(item.id, "seuilStock", formData.seuilStock);
    onClose();
  };

  if (!isOpen) return null;

  // Unités nécessitant une précision supplémentaire
  const needsPrecision = ["caisse", "boîte", "baril", "sac"].includes(formData.formatUnite);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-[var(--card)] rounded-xl shadow-xl border border-[var(--border)] w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[var(--card)] border-b border-[var(--border)] px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-[var(--foreground)]">
            Modifier le produit
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[var(--muted)] rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informations de base */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[var(--muted-foreground)] uppercase tracking-wide">
              Informations de base
            </h3>

            {/* Nom */}
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
                Nom du produit
              </label>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-steel-dark)] text-[var(--foreground)]"
                required
              />
            </div>

            {/* Catégorie */}
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
                Catégorie
              </label>
              <select
                value={formData.categorie}
                onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-steel-dark)] text-[var(--foreground)]"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Type d'item */}
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
                Type d'item
              </label>
              <select
                value={formData.typeItem}
                onChange={(e) => setFormData({ ...formData, typeItem: e.target.value as any })}
                className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-steel-dark)] text-[var(--foreground)]"
              >
                <option value="produit_simple">Produit (se vend tel quel)</option>
                <option value="ingredient">Ingrédient (utilisé dans une recette)</option>
                <option value="recette">Recette (mélange d'ingrédients)</option>
              </select>
              <p className="mt-1.5 text-xs text-[var(--muted-foreground)]">
                Un item peut être à la fois produit et ingrédient
              </p>
            </div>
          </div>

          {/* Format et contenance */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[var(--muted-foreground)] uppercase tracking-wide">
              Format et contenance
            </h3>
            <p className="text-sm text-[var(--muted-foreground)]">
              Définissez la contenance d'une unité de ce produit. Par exemple, une bouteille de rhum = 750ml.
            </p>

            <div className="grid grid-cols-2 gap-4">
              {/* Contenance */}
              <div>
                <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
                  Contenance
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.formatQuantite}
                  onChange={(e) => setFormData({ ...formData, formatQuantite: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-steel-dark)] text-[var(--foreground)]"
                  placeholder="Ex: 750"
                />
              </div>

              {/* Unité */}
              <div>
                <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
                  Unité de mesure
                </label>
                <select
                  value={formData.formatUnite}
                  onChange={(e) => setFormData({ ...formData, formatUnite: e.target.value })}
                  className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-steel-dark)] text-[var(--foreground)]"
                >
                  {UNITES_MESURE.map((unite) => (
                    <option key={unite.value} value={unite.value}>
                      {unite.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Affichage du format complet */}
            {formData.formatQuantite > 0 && (
              <div className="p-3 bg-[var(--color-steel-bg)] border border-[var(--color-steel-dark)]/20 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[var(--muted-foreground)]">Format:</span>
                  <span className="font-semibold text-[var(--color-steel-dark)]">
                    1 unité = {formData.formatQuantite} {formData.formatUnite}
                  </span>
                </div>
              </div>
            )}

            {/* Précision supplémentaire */}
            {needsPrecision && (
              <div>
                <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
                  Précision supplémentaire
                </label>
                <input
                  type="text"
                  value={formData.formatPrecision}
                  onChange={(e) => setFormData({ ...formData, formatPrecision: e.target.value })}
                  className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-steel-dark)] text-[var(--foreground)]"
                  placeholder="Ex: de 12 bouteilles"
                />
              </div>
            )}
          </div>

          {/* Seuils de stock */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[var(--muted-foreground)] uppercase tracking-wide">
              Seuils de stock
            </h3>
            <p className="text-sm text-[var(--muted-foreground)]">
              Définissez les seuils de stock pour ce produit.
            </p>

            <div className="grid grid-cols-2 gap-4">
              {/* Seuil de stock */}
              <div>
                <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
                  Seuil de stock
                </label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={formData.seuilStock}
                  onChange={(e) => setFormData({ ...formData, seuilStock: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-steel-dark)] text-[var(--foreground)]"
                  placeholder="Ex: 10"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--border)]">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-[var(--muted)] hover:bg-[var(--switch-background)] text-[var(--foreground)] rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[var(--color-steel)] hover:bg-[var(--color-steel-dark)] text-white rounded-lg transition-colors"
            >
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}